import { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Tooltip } from '@radix-ui/react-tooltip';
import { Button } from '@/comps/primitives/button';
import { TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { FilterPickerDraftActions } from '@/comps/FilterPicker/FilterPicker';
import { cn } from '@/utils/shad';
import { WithClassName } from '@/types/component';

type Props = WithClassName & {
  children: ReactNode;
  onNewFilter?: () => void;
  onSave?: () => void;
  showSave?: boolean;
  saveDisabled?: boolean;
  saveTooltip?: string;
  onApply?: () => void;
  applyDisabled?: boolean;
  showApply?: boolean;
  /** Report-only: show Reset when local filters/timerange differ from DB. */
  showReset?: boolean;
  onReset?: () => void;
  showDraftActions?: boolean;
  draftActions?: FilterPickerDraftActions;
};

export function FilterContentV2Panel({
  children,
  onNewFilter,
  onSave,
  showSave = true,
  saveDisabled,
  saveTooltip,
  onApply,
  applyDisabled,
  showApply = true,
  showReset,
  onReset,
  showDraftActions,
  draftActions,
  className,
}: Props) {
  const saveAsButton = (
    <button
      type="button"
      className={cn(
        'text-muted-foreground transition-colors hover:text-white disabled:opacity-40',
        saveTooltip ? 'disabled:pointer-events-auto disabled:cursor-not-allowed' : 'disabled:pointer-events-none',
      )}
      onClick={onSave}
      disabled={saveDisabled}
    >
      Save as
    </button>
  );

  const showFooter = showApply || (showDraftActions && !!draftActions);
  const draftSaveLabel = draftActions?.saveLabel ?? 'Apply for everyone';
  const defaultDraftSaveTooltip = 'Apply the current filters and time range for all users viewing this report';
  const draftSaveTooltip =
    !draftActions?.canSaveForEveryone && draftActions?.saveDisabledMessage
      ? draftActions.saveDisabledMessage
      : (draftActions?.saveTooltip ?? defaultDraftSaveTooltip);

  return (
    <div
      data-tour="filter-v2-panel"
      className={cn(
        'flex min-w-0 flex-col rounded-lg border border-zinc-800 bg-[#09090B]',
        !showFooter && 'pb-4',
        className,
      )}
    >
      <div className="flex flex-col gap-3 px-3 pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-white">Active filters</span>
          <div className="flex items-center gap-2 text-[12px] font-semibold">
            {showReset && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-destructive transition-colors hover:text-destructive/80"
                      onClick={onReset}
                    >
                      Reset
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Reset the time range and filters to the report&apos;s default settings.
                  </TooltipContent>
                </Tooltip>
                {showSave && (
                  <span className="text-zinc-700" aria-hidden>
                    |
                  </span>
                )}
              </>
            )}
            {showSave &&
              (saveTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">{saveAsButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>{saveTooltip}</TooltipContent>
                </Tooltip>
              ) : (
                saveAsButton
              ))}
          </div>
        </div>

        {children}

        <Button
          type="button"
          variant="outline"
          className="h-8 w-full justify-center gap-1.5 rounded-base border-dashed border-zinc-700 bg-transparent text-[12px] font-semibold text-muted-foreground hover:bg-zinc-900 hover:text-white"
          onClick={onNewFilter}
        >
          <Plus className="h-3.5 w-3.5" />
          Add filter
        </Button>
      </div>

      {showFooter ? (
        <div className="mt-3 flex items-center justify-end gap-3 border-t border-zinc-800 px-3 pt-3 pb-4">
          {showDraftActions && draftActions && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    type="button"
                    className="text-[12px] font-semibold text-muted-foreground transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-40"
                    disabled={!draftActions.canSaveForEveryone || draftActions.isSaving}
                    onClick={() => draftActions.onSaveForEveryone()}
                  >
                    {draftActions.isSaving ? 'Saving...' : draftSaveLabel}
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{draftSaveTooltip}</TooltipContent>
            </Tooltip>
          )}
          {showApply ? (
            <Button
              type="button"
              variant="white"
              size="sm"
              className="h-8 min-w-18 rounded-base px-4 text-[12px] font-semibold"
              onClick={onApply}
              disabled={applyDisabled}
            >
              Apply
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
